<template>
  <div class="app-nav-root">
    <header class="app-nav" :class="{ 'nav-scrolled': isScrolled }">
      <v-container>
        <div class="nav-content">
          <div class="nav-brand" @click="navigateTo('/')">
            <div class="nav-logo-icon">
              <v-icon color="#0A0E14" size="22">mdi-dumbbell</v-icon>
            </div>
            <div class="nav-logo-text">
              <div class="nav-name">GymDesk</div>
              <div class="nav-suite">SUITE</div>
            </div>
          </div>

          <nav class="nav-links">
            <router-link to="/" class="nav-link" exact>Home</router-link>
            <router-link to="/why" class="nav-link">Why GymDesk Suite</router-link>
            <router-link to="/features" class="nav-link">Features</router-link>
            <router-link to="/about" class="nav-link">About</router-link>
            <router-link to="/blog" class="nav-link">Blog</router-link>
          </nav>

          <div class="nav-actions">
            <v-btn outlined class="nav-btn-login" to="/login">
              Login
            </v-btn>
            <v-btn depressed class="nav-btn-demo" @click="requestDemo">
              Request Demo
            </v-btn>
          </div>

          <button class="nav-burger" aria-label="Open menu" @click="drawer = true">
            <v-icon color="white">mdi-menu</v-icon>
          </button>
        </div>
      </v-container>
    </header>

    <!-- Mobile Drawer — deliberately a SIBLING of <header>, not nested inside
         it. .app-nav uses backdrop-filter, which creates a new containing
         block for position:fixed descendants, so a drawer inside <header>
         would be clipped to the header's height. -->
    <div v-if="drawer" class="drawer-overlay" @click="drawer = false">
      <div class="drawer-panel" @click.stop>
        <div class="drawer-header">
          <div class="drawer-logo">
            <v-icon color="#0A0E14" size="24">mdi-dumbbell</v-icon>
          </div>
          <div class="drawer-brand">
            <div>GymDesk</div>
            <div class="brand-sub">SUITE</div>
          </div>
          <button class="drawer-close" aria-label="Close menu" @click="drawer = false">
            <v-icon>mdi-close</v-icon>
          </button>
        </div>
        <v-divider class="my-4"></v-divider>
        <div class="drawer-nav">
          <a class="drawer-link" @click="navigateTo('/')">
            <v-icon left>mdi-home</v-icon> Home
          </a>
          <a class="drawer-link" @click="navigateTo('/why')">
            <v-icon left>mdi-star-four-points</v-icon> Why GymDesk Suite
          </a>
          <a class="drawer-link" @click="navigateTo('/features')">
            <v-icon left>mdi-view-grid</v-icon> Features
          </a>
          <a class="drawer-link" @click="navigateTo('/about')">
            <v-icon left>mdi-information</v-icon> About
          </a>
          <a class="drawer-link" @click="navigateTo('/blog')">
            <v-icon left>mdi-post</v-icon> Blog
          </a>
          <v-divider class="my-4"></v-divider>
          <a class="drawer-link" @click="navigateTo('/login')">
            <v-icon left>mdi-login</v-icon> Login
          </a>
          <a class="drawer-link drawer-link-ember" @click="requestDemo">
            <v-icon left>mdi-calendar-check</v-icon> Request Demo
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppNavigation',
  data() {
    return {
      drawer: false,
      isScrolled: false
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 20;
    },
    navigateTo(route) {
      this.drawer = false;
      if (this.$route.path === route) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      this.$router.push(route);
    },
    // Request Demo opens the demo page.
    requestDemo() {
      this.drawer = false;
      this.$router.push('/demo').catch(() => {});
    }
  }
};
</script>

<style scoped>

.app-nav-root {
  display: contents;
}

.app-nav {
  --border-subtle: rgba(226, 232, 240, 0.09);
  --text-primary: #F1F3F6;
  --text-secondary: rgba(241, 243, 246, 0.7);
  --ember: #FF7A45;
  --ember-light: #FFB07A;
  --ember-dark: #D9501E;

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 199; /* below Vuetify's dialog overlay (201+) */
  background: rgba(10, 14, 20, 0.86);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border-subtle);
  font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: background 0.3s ease, box-shadow 0.3s ease;
}

.app-nav.nav-scrolled {
  background: rgba(7, 9, 13, 0.96);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}

.app-nav ::v-deep .v-btn {
  text-transform: none !important;
  letter-spacing: 0.01em;
}

.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.nav-brand:hover {
  transform: scale(1.03);
}

.nav-logo-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(140deg, var(--ember-light) 0%, var(--ember) 55%, var(--ember-dark) 100%);
}

.nav-logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.nav-name {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 1.35rem;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: -0.01em;
}

.nav-suite {
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 3px;
  color: var(--ember);
  margin-top: 2px;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 6px;
}

.nav-link {
  padding: 10px 16px;
  color: var(--text-secondary);
  font-size: 15px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.nav-link:hover,
.nav-link.router-link-active {
  color: var(--ember-light);
  background: rgba(255, 122, 69, 0.08);
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.nav-btn-login {
  border: 1.5px solid var(--ember) !important;
  color: var(--ember-light) !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
}

.nav-btn-demo {
  background: linear-gradient(150deg, var(--ember-light) 0%, var(--ember) 55%, var(--ember-dark) 100%) !important;
  color: #0A0E14 !important;
  font-weight: 600 !important;
  border-radius: 10px !important;
}

.nav-burger {
  display: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  cursor: pointer;
}

/* ==================== MOBILE DRAWER ==================== */
.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  z-index: 200;
}

.drawer-panel {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 300px;
  max-width: 86vw;
  background: #0F141C;
  border-right: 1px solid rgba(226, 232, 240, 0.09);
  box-shadow: 0 0 60px rgba(0, 0, 0, 0.5);
  overflow-y: auto;
  padding: 8px;
  font-family: 'Outfit', sans-serif;
}

.drawer-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 16px 0;
}

.drawer-logo {
  width: 44px;
  height: 44px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(140deg, #FFB07A 0%, #FF7A45 55%, #D9501E 100%);
}

.drawer-brand {
  flex: 1;
  line-height: 1.15;
}

.drawer-brand > div:first-child {
  font-family: 'Fraunces', Georgia, serif;
  font-size: 20px;
  font-weight: 600;
  color: #F1F3F6;
}

.brand-sub {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 3px;
  color: #FF7A45;
}

.drawer-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.09);
  border-radius: 8px;
  color: rgba(241, 243, 246, 0.7);
  cursor: pointer;
}

.drawer-close ::v-deep .v-icon {
  color: currentColor !important;
}

.drawer-nav {
  padding: 8px 16px 16px;
}

.drawer-link {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  color: rgba(241, 243, 246, 0.7);
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 4px;
}

.drawer-link ::v-deep .v-icon {
  color: currentColor !important;
}

.drawer-link:hover {
  background: rgba(255, 122, 69, 0.1);
  color: #FFB07A;
  transform: translateX(8px);
}

.drawer-link-ember {
  color: #FFB07A;
}

@media (max-width: 960px) {
  .nav-links,
  .nav-actions {
    display: none;
  }

  .nav-burger {
    display: flex;
  }
}
</style>